/**
 * lib/lang.jsx — Phase 5 translation layer (frontend).
 *
 * A tiny language context + a generic "translate any data object" hook so ANY
 * module can be shown in the learner's chosen language without re-authoring
 * content. Prose strings are batched to POST /api/translate (cached server-
 * side); slugs, urls, numbers and keys are left untouched.
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "./api";

const KEY = "maldek.lang.v1";
const LangCtx = createContext(["en", () => {}]);

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem(KEY) || "en"; } catch { return "en"; }
  });
  const setLang = (l) => {
    setLangState(l);
    try { localStorage.setItem(KEY, l); } catch { /* ignore */ }
  };
  return <LangCtx.Provider value={[lang, setLang]}>{children}</LangCtx.Provider>;
}

export function useLang() {
  return useContext(LangCtx);
}

export const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "zh", label: "中文" },
  { code: "fr", label: "Français" },
  { code: "ht", label: "Kreyòl" },
  { code: "ar", label: "العربية" },
  { code: "pt", label: "Português" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "tl", label: "Tagalog" },
  { code: "ru", label: "Русский" },
];

// Heuristic: is this string human prose worth translating (vs a slug/url/id)?
function isProse(s) {
  if (typeof s !== "string") return false;
  const t = s.trim();
  if (t.length < 3) return false;
  if (/^https?:\/\//i.test(t)) return false;         // urls
  if (/^[a-z0-9_-]+$/i.test(t) && !t.includes(" ")) return false; // slugs/ids
  return /\s/.test(t) || t.length > 24;              // has a space or is longish
}

function collectStrings(node, out) {
  if (typeof node === "string") { if (isProse(node)) out.add(node); return; }
  if (Array.isArray(node)) { node.forEach((v) => collectStrings(v, out)); return; }
  if (node && typeof node === "object") { Object.values(node).forEach((v) => collectStrings(v, out)); }
}

function rebuild(node, map) {
  if (typeof node === "string") return map[node] ?? node;
  if (Array.isArray(node)) return node.map((v) => rebuild(v, map));
  if (node && typeof node === "object") {
    const o = {};
    for (const k of Object.keys(node)) o[k] = rebuild(node[k], map);
    return o;
  }
  return node;
}

export async function translateBatch(texts, lang) {
  if (!texts.length || lang === "en") return texts;
  const r = await api.post("/translate", { texts, lang });
  return r.data?.translations || texts;
}

/**
 * useTranslatedData(raw, lang) — returns { data, translating }.
 * When lang === 'en' or raw is null, returns raw untouched. Otherwise it
 * collects prose strings, batch-translates them, and returns a deep copy
 * with the translations swapped in.
 */
export function useTranslatedData(raw, lang) {
  const [data, setData] = useState(raw);
  const [translating, setTranslating] = useState(false);
  const cache = useRef({}); // lang -> string map

  useEffect(() => {
    let alive = true;
    if (!raw || lang === "en") { setData(raw); setTranslating(false); return; }

    const uniq = new Set();
    collectStrings(raw, uniq);
    const all = [...uniq];
    const known = cache.current[lang] || {};
    const missing = all.filter((s) => !(s in known));

    if (missing.length === 0) {
      setData(rebuild(raw, known));
      setTranslating(false);
      return;
    }

    setTranslating(true);
    // chunk to keep each request reasonable
    const chunks = [];
    for (let i = 0; i < missing.length; i += 25) chunks.push(missing.slice(i, i + 25));

    (async () => {
      const map = { ...known };
      for (const chunk of chunks) {
        try {
          const out = await translateBatch(chunk, lang);
          chunk.forEach((src, i) => { map[src] = out[i] ?? src; });
        } catch { chunk.forEach((src) => { map[src] = src; }); }
      }
      if (!alive) return;
      cache.current[lang] = map;
      setData(rebuild(raw, map));
      setTranslating(false);
    })();

    return () => { alive = false; };
  }, [raw, lang]);

  return { data: data || raw, translating };
}
